
import { PROMPT_IDS, PROMPT_CSS_CLASSES } from "./constants";
import {
  findOrCreateElementById,
  removeDomElementById,
  addDomElement,
  isMobile,
  addCssClass,
  addCssStyle
} from "./utils";
import { PromptOptions } from "./types";

export default class Prompt {
  options: PromptOptions;

  constructor(options: PromptOptions) {
    this.options = options;
  }

  /** Accessors */
  get container(): Element {
    return findOrCreateElementById(PROMPT_IDS.container);
  }

  get dialog(): Element {
    return findOrCreateElementById(PROMPT_IDS.dialog);
  }

  get allowButton(): HTMLButtonElement {
    return findOrCreateElementById(PROMPT_IDS.allowButton, 'button') as HTMLButtonElement;
  }

  get cancelButton(): HTMLButtonElement {
    return findOrCreateElementById(PROMPT_IDS.cancelButton, 'button') as HTMLButtonElement;
  }

  /** Rendering Methods */
  display(): void {
    // Remove previously created prompt containers
    removeDomElementById(PROMPT_IDS.container);

    // Add the new container with the dialog
    addDomElement('body', this.getPromptHtml());

    if (this.options.customCssStyle) {
      addCssStyle(this.options.customCssStyle);
    }

    // Animate the prompt depending on the environment
    addCssClass(this.container, isMobile() ? 'slide-up' : 'slide-down');

    if (this.shouldDisplayButtons()) {
      // Bind events to the allow and cancel buttons
      this.allowButton.addEventListener('click', this.handlePromptAllowed, { once: true });
      this.cancelButton.addEventListener('click', this.handlePromptCancelled, { once: true });
    }
  }

  private getPromptHtml(): string {
    const {
      messageText,
      allowButtonText,
      cancelButtonText,
      promptContainerClass = '',
    } = this.options;

    const containerClass =
      `${PROMPT_CSS_CLASSES.container} ${PROMPT_CSS_CLASSES.reset} ${promptContainerClass}`;
    const allowButtonClass = 'primary slidedown-button';
    const cancelButtonClass = 'secondary slidedown-button';

    const footerHtml = `
      <div class="slidedown-footer" id="slidedown-footer">
        <button id="${PROMPT_IDS.cancelButton}" class="${cancelButtonClass}">
          ${cancelButtonText}
        </button>
        <button id="${PROMPT_IDS.allowButton}" class="${allowButtonClass}">
          ${allowButtonText}
        </button>
      </div>
    `;

    return `
      <div id=${PROMPT_IDS.container} class="${containerClass}">
        <div id=${PROMPT_IDS.dialog} class="onesignal-slidedown-dialog">
          <div id="shopify-onesignal-slidedown">
            <div class="slidedown-body" id="slidedown-body">
              <div class="slidedown-body-message">${messageText}</div>
              <div id="onesignal-loading-container"></div>
            </div>
            ${this.shouldDisplayButtons() ? footerHtml : ''}
          </div>
        </div>
      </div>
    `;
  }

  /** Handlers */
  private handlePromptCancelled = (): void => {
    this.close();

    if (this.options.onCancel) {
      this.options.onCancel();
    }
  }

  private handlePromptAllowed = (): void => {
    this.close();

    if (this.options.onAllow) {
      this.options.onAllow();
    }
  }

  close = (): void => {
    addCssClass(this.container, 'close-slidedown');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.dialog.addEventListener('animationend', (event: any) => {
      if (event.animationName === 'slideDownExit' || event.animationName === 'slideUpExit') {
        removeDomElementById(PROMPT_IDS.container);
      }
    }, { once: true});
  }

  /** Helpers */
  private shouldDisplayButtons(): boolean {
    return !!this.options.allowButtonText && !!this.options.cancelButtonText;
  }

}
